/** The Pay rates card on the staff page: each role's current rate, its history,
 *  and, for people allowed to change pay, the controls to do so. */
import PayRateRoleBlock from './PayRateRoleBlock';
import PayRatesCardFrame from './PayRatesCardFrame';
import PayRatesLoadState from './PayRatesLoadState';
import SalariedSwitch from './SalariedSwitch';
import { useStaffPayRates } from './use-staff-pay-rates';

export default function StaffPayRatesCard({
  userId,
  memberName,
  onSalariedChanged,
}: {
  userId: string;
  memberName: string;
  onSalariedChanged: () => void;
}) {
  const state = useStaffPayRates(userId);
  const { payRates, actionError } = state;

  if (state.hidden) return null;
  if (!payRates) return <PayRatesLoadState loadError={state.loadError} onRetry={state.retry} />;

  async function changeSalaried(next: boolean): Promise<boolean> {
    const result = await state.setSalaried(next);
    if (result.ok) onSalariedChanged();
    return result.ok;
  }

  const errorFor = (scope: string) => (actionError?.scope === scope ? actionError.message : null);

  return (
    <PayRatesCardFrame>
      {payRates.canEdit ? (
        <SalariedSwitch
          isSalaried={payRates.isSalaried}
          memberName={memberName}
          busy={state.busy}
          error={errorFor('salaried')}
          onConfirm={changeSalaried}
        />
      ) : (
        <p className="text-sm text-stone-600 dark:text-stone-400">
          You can view these rates but not change them.
        </p>
      )}
      <div className="mt-2 divide-y divide-stone-100 dark:divide-stone-800">
        {payRates.roles.map((rates) => (
          <PayRateRoleBlock
            key={rates.role}
            rates={rates}
            payRates={payRates}
            busy={state.busy}
            error={errorFor(rates.role)}
            onSetRate={state.setRate}
            onRemove={(rateId) => state.removeRate(rates.role, rateId)}
          />
        ))}
      </div>
    </PayRatesCardFrame>
  );
}
